import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiCall, ENDPOINTS } from '../config/api';
import { FiX, FiUser, FiUsers } from 'react-icons/fi';
import { EmailRecipient } from '../types/email';

interface Patient {
	id: number;
	first_name: string;
	last_name: string;
	email: string;
	contact_email?: string;
	medical_record_number?: string;
	date_of_birth?: string;
}

interface Contact {
	id: number;
	full_name?: string;
	salutation?: string;
	first_name: string;
	last_name: string;
	company: string;
	email: string;
	phone?: string;
	mobile?: string;
	fax?: string;
	address_line_1?: string;
	address_line_2?: string;
	suburb?: string;
	state?: string;
	postcode?: string;
	country?: string;
	specialty?: string;
	contact_type_name?: string;
}

interface RecipientSelectorProps {
	label: string;
	selectedRecipients: EmailRecipient[];
	onChange: (recipients: EmailRecipient[]) => void;
	placeholder?: string;
	allowMultiple?: boolean;
	compact?: boolean;
	includePatients?: boolean;  // Whether to search patients (default: false)
	requireEmail?: boolean;      // Whether email address is required (default: true for email, false for letters)
}

const RecipientSelector: React.FC<RecipientSelectorProps> = ({
	label,
	selectedRecipients,
	onChange,
	placeholder = 'Search contacts...',
	allowMultiple = true,
	compact = false,
	includePatients = false,
	requireEmail = true
}) => {
	const { token } = useAuth();
	const [searchTerm, setSearchTerm] = useState('');
	const [showResults, setShowResults] = useState(false);
	const [patientResults, setPatientResults] = useState<Patient[]>([]);
	const [contactResults, setContactResults] = useState<Contact[]>([]);
	const [loading, setLoading] = useState(false);
	const searchRef = useRef<HTMLDivElement>(null);

	// Click outside to close dropdown
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
				setShowResults(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Search with debounce
	useEffect(() => {
		if (searchTerm.length >= 2 && token) {
			const timer = setTimeout(async () => {
				await performSearch();
			}, 300);
			
			return () => clearTimeout(timer);
		} else {
			setPatientResults([]);
			setContactResults([]);
			setShowResults(false);
		}
	}, [searchTerm, token]);

	const performSearch = async () => {
		if (!token) return;
		
		setLoading(true);
		try {
			// Search patients only if includePatients is true
			if (includePatients) {
				const patientResponse = await apiCall(
					`${ENDPOINTS.PATIENTS.PATIENTS}?search=${encodeURIComponent(searchTerm)}`,
					{ method: 'GET' },
					token
				);
				
				if (patientResponse.ok) {
					const patientData = await patientResponse.json();
					const patients = patientData.results || patientData || [];
					// Filter patients with valid emails only if requireEmail is true
					setPatientResults(requireEmail 
						? patients.filter((p: Patient) => p.email || p.contact_email)
						: patients
					);
				}
			} else {
				setPatientResults([]);
			}

			// Always search contacts
			const contactResponse = await apiCall(
				`${ENDPOINTS.CORE.CONTACTS}?search=${encodeURIComponent(searchTerm)}`,
				{ method: 'GET' },
				token
			);
			
			if (contactResponse.ok) {
				const contactData = await contactResponse.json();
				const contacts = contactData.results || contactData || [];
				// Filter contacts with valid emails only if requireEmail is true
				setContactResults(requireEmail 
					? contacts.filter((c: Contact) => c.email)
					: contacts
				);
			}

			setShowResults(true);
		} catch (error) {
			console.error('Error searching recipients:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleSelectPatient = (patient: Patient) => {
		const recipient: EmailRecipient = {
			id: patient.id,
			email: patient.email || patient.contact_email || '',
			name: `${patient.first_name} ${patient.last_name}`,
			type: 'patient',
			date_of_birth: patient.date_of_birth,
			medical_record_number: patient.medical_record_number
		};

		addRecipient(recipient);
	};

	const handleSelectContact = (contact: Contact) => {
		const recipient: EmailRecipient = {
			id: contact.id,
			email: contact.email || '',
			name: contact.full_name || `${contact.first_name} ${contact.last_name}`.trim() || contact.company,
			type: 'contact',
			// Capture ALL contact fields for letter generation
			salutation: contact.salutation,
			first_name: contact.first_name,
			last_name: contact.last_name,
			company: contact.company,
			organization: contact.company,  // alias
			phone: contact.phone,
			mobile: contact.mobile,
			fax: contact.fax,
			address_line_1: contact.address_line_1,
			address_line_2: contact.address_line_2,
			suburb: contact.suburb,
			state: contact.state,
			postcode: contact.postcode,
			country: contact.country,
			specialty: contact.specialty,
			contact_type_name: contact.contact_type_name
		};

		addRecipient(recipient);
	};

	const addRecipient = (recipient: EmailRecipient) => {
		// Check if already selected
		const alreadySelected = selectedRecipients.some(r => 
			r.email.toLowerCase() === recipient.email.toLowerCase()
		);

		if (alreadySelected) {
			alert('This recipient is already selected');
			return;
		}

		if (allowMultiple) {
			onChange([...selectedRecipients, recipient]);
		} else {
			onChange([recipient]);
		}

		setSearchTerm('');
		setShowResults(false);
		setPatientResults([]);
		setContactResults([]);
	};

	const handleRemoveRecipient = (email: string) => {
		onChange(selectedRecipients.filter(r => r.email !== email));
	};

	const hasResults = patientResults.length > 0 || contactResults.length > 0;

	if (compact) {
		return (
			<div className="relative" ref={searchRef}>
				{/* Input field with inline chips */}
				<div className="input input-bordered w-full min-h-[3rem] flex flex-wrap items-center gap-2 p-2">
					{/* Selected Recipients (Chips) */}
					{selectedRecipients.map((recipient) => {
						const isPatient = recipient.type === 'patient';
						const isContact = recipient.type === 'contact' && recipient.name !== recipient.email;
						const isFreeform = recipient.name === recipient.email;
						
						let badgeColor = 'bg-primary text-primary-content';
						if (isPatient) badgeColor = 'bg-info text-info-content';
						else if (isContact) badgeColor = 'bg-success text-success-content';
						else if (isFreeform) badgeColor = 'bg-warning text-warning-content';
						
						return (
							<div
								key={recipient.email}
								className={`flex items-center gap-1 ${badgeColor} px-2 py-1 rounded text-sm font-medium shrink-0 whitespace-nowrap`}
							>
								{isPatient && <FiUser className="w-3 h-3" />}
								{isContact && <FiUsers className="w-3 h-3" />}
								<span>{recipient.name}</span>
								{!isFreeform && <span className="opacity-70 text-xs">({recipient.email})</span>}
								<button
									type="button"
									onClick={() => handleRemoveRecipient(recipient.email)}
									className="hover:bg-base-content/20 rounded-full p-0.5"
								>
									<FiX className="w-3 h-3" />
								</button>
							</div>
						);
					})}
					
					{/* Search Input */}
					<input
						type="text"
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						onKeyDown={(e) => {
							// Allow adding free-form email addresses with Enter, comma, or semicolon
							if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
								e.preventDefault();
								const email = searchTerm.trim();
								if (email && email.includes('@')) {
									const recipient: EmailRecipient = {
										id: Date.now(),
										email: email,
										name: email,
										type: 'contact'
									};
									addRecipient(recipient);
								}
							}
						}}
						onFocus={() => {
							if (hasResults) setShowResults(true);
						}}
						onBlur={() => {
							// Add email on blur if valid
							setTimeout(() => {
								const email = searchTerm.trim();
								if (email && email.includes('@')) {
									const recipient: EmailRecipient = {
										id: Date.now(),
										email: email,
										name: email,
										type: 'contact'
									};
									addRecipient(recipient);
								}
							}, 200);
						}}
					placeholder={selectedRecipients.length === 0 ? placeholder : ''}
					className="flex-1 min-w-[120px] outline-none bg-transparent border-0 focus:outline-none focus:ring-0 h-8"
					style={{ padding: 0 }}
				/>					{loading && (
						<span className="loading loading-spinner loading-sm shrink-0"></span>
					)}
				</div>				{/* Search Results Dropdown */}
				{showResults && hasResults && (
						<div className="absolute top-full left-0 right-0 mt-1 bg-base-100 rounded-lg shadow-xl border border-base-300 max-h-[400px] overflow-y-auto z-50">
							{/* Patients Section */}
							{patientResults.length > 0 && (
								<div>
									<div className="px-4 py-2 bg-base-200 font-semibold text-sm flex items-center gap-2">
										<FiUser className="w-4 h-4" />
										Patients ({patientResults.length})
									</div>
									{patientResults.map((patient) => (
										<div
											key={`patient-${patient.id}`}
											onClick={() => handleSelectPatient(patient)}
											className="px-4 py-3 hover:bg-base-200 cursor-pointer border-b border-base-300 last:border-b-0"
										>
											<div className="font-medium">
												{patient.first_name} {patient.last_name}
											</div>
											<div className="text-sm opacity-60 space-y-1 mt-1">
												<div>Email: {patient.email || patient.contact_email}</div>
												{patient.date_of_birth && (
													<div>DOB: {patient.date_of_birth}</div>
												)}
												{patient.medical_record_number && (
													<div>MRN: {patient.medical_record_number}</div>
												)}
											</div>
										</div>
									))}
								</div>
							)}

							{/* Contacts Section */}
							{contactResults.length > 0 && (
								<div>
									<div className="px-4 py-2 bg-base-200 font-semibold text-sm flex items-center gap-2">
										<FiUsers className="w-4 h-4" />
										Contacts ({contactResults.length})
									</div>
									{contactResults.map((contact) => (
										<div
											key={`contact-${contact.id}`}
											onClick={() => handleSelectContact(contact)}
											className="px-4 py-3 hover:bg-base-200 cursor-pointer border-b border-base-300 last:border-b-0"
										>
											<div className="font-medium">
												{contact.full_name || `${contact.first_name} ${contact.last_name}`.trim() || contact.company}
											</div>
											<div className="text-sm opacity-60 space-y-1 mt-1">
												<div>Email: {contact.email}</div>
												{contact.company && contact.full_name && (
													<div>Company: {contact.company}</div>
												)}
												{contact.contact_type_name && (
													<div>Type: {contact.contact_type_name}</div>
												)}
											</div>
										</div>
									))}
								</div>
							)}
						</div>
					)}

				{/* No Results Message */}
				{showResults && !hasResults && searchTerm.length >= 2 && !loading && (
					<div className="absolute top-full left-0 right-0 mt-1 bg-base-100 rounded-lg shadow-xl border border-base-300 p-4 text-center text-sm opacity-60 z-50">
						No patients or contacts found with email addresses
					</div>
				)}
			</div>
		);
	}

	return (
		<div className="form-control">
			<label className="label">
				<span className="label-text font-semibold">{label}</span>
				{selectedRecipients.length > 0 && (
					<span className="label-text-alt opacity-60">
						{selectedRecipients.length} selected
					</span>
				)}
			</label>

			{/* Selected Recipients (Chips) */}
			{selectedRecipients.length > 0 && (
				<div className="flex flex-wrap gap-2 mb-2">
					{selectedRecipients.map((recipient) => (
						<div
							key={recipient.email}
							className="flex items-center gap-2 bg-primary text-primary-content px-3 py-1 rounded-full text-sm font-medium"
						>
							{recipient.type === 'patient' ? (
								<FiUser className="w-3 h-3" />
							) : (
								<FiUsers className="w-3 h-3" />
							)}
							<span>{recipient.name}</span>
							<span className="opacity-80 text-xs">({recipient.email})</span>
							<button
								type="button"
								onClick={() => handleRemoveRecipient(recipient.email)}
								className="hover:bg-base-content/20 rounded-full p-0.5 ml-1"
							>
								<FiX className="w-3 h-3" />
							</button>
						</div>
					))}
				</div>
			)}

			{/* Search Input */}
			<div className="relative" ref={searchRef}>
				<input
					type="text"
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					onFocus={() => {
						if (hasResults) setShowResults(true);
					}}
					placeholder={placeholder}
					className="input input-bordered w-full"
				/>

				{loading && (
					<div className="absolute right-3 top-1/2 -translate-y-1/2">
						<span className="loading loading-spinner loading-sm"></span>
					</div>
				)}

				{/* Search Results Dropdown */}
				{showResults && hasResults && (
					<div className="absolute top-full left-0 right-0 mt-1 bg-base-100 rounded-lg shadow-xl border border-base-300 max-h-[400px] overflow-y-auto z-50">
						{/* Patients Section */}
						{patientResults.length > 0 && (
							<div>
								<div className="px-4 py-2 bg-base-200 font-semibold text-sm flex items-center gap-2">
									<FiUser className="w-4 h-4" />
									Patients ({patientResults.length})
								</div>
								{patientResults.map((patient) => (
									<div
										key={`patient-${patient.id}`}
										onClick={() => handleSelectPatient(patient)}
										className="px-4 py-3 hover:bg-base-200 cursor-pointer border-b border-base-300 last:border-b-0"
									>
										<div className="font-medium">
											{patient.first_name} {patient.last_name}
										</div>
										<div className="text-sm opacity-60 space-y-1 mt-1">
											<div>Email: {patient.email || patient.contact_email}</div>
											{patient.date_of_birth && (
												<div>DOB: {patient.date_of_birth}</div>
											)}
											{patient.medical_record_number && (
												<div>MRN: {patient.medical_record_number}</div>
											)}
										</div>
									</div>
								))}
							</div>
						)}

						{/* Contacts Section */}
						{contactResults.length > 0 && (
							<div>
								<div className="px-4 py-2 bg-base-200 font-semibold text-sm flex items-center gap-2">
									<FiUsers className="w-4 h-4" />
									Contacts ({contactResults.length})
								</div>
								{contactResults.map((contact) => (
									<div
										key={`contact-${contact.id}`}
										onClick={() => handleSelectContact(contact)}
										className="px-4 py-3 hover:bg-base-200 cursor-pointer border-b border-base-300 last:border-b-0"
									>
										<div className="font-medium">
											{contact.full_name || `${contact.first_name} ${contact.last_name}`.trim() || contact.company}
										</div>
										<div className="text-sm opacity-60 space-y-1 mt-1">
											<div>Email: {contact.email}</div>
											{contact.company && contact.full_name && (
												<div>Company: {contact.company}</div>
											)}
											{contact.contact_type_name && (
												<div>Type: {contact.contact_type_name}</div>
											)}
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				)}

				{/* No Results Message */}
				{showResults && !hasResults && searchTerm.length >= 2 && !loading && (
					<div className="absolute top-full left-0 right-0 mt-1 bg-base-100 rounded-lg shadow-xl border border-base-300 p-4 text-center text-sm opacity-60 z-50">
						No patients or contacts found with email addresses
					</div>
				)}
			</div>
		</div>
	);
};

export default RecipientSelector;
